import { Head, router } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import PageHeader from '@/Components/PageHeader';
import FormSelect from '@/Components/FormSelect';
import Button from '@/Components/Button';
import {
    CalendarDaysIcon,
    ArrowDownTrayIcon,
    DocumentChartBarIcon,
    CheckCircleIcon,
    ClockIcon,
    LockClosedIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon,
} from '@heroicons/react/24/outline';

export default function Index({ months, selectedYear, currentYear, availableYears }) {
    const [syncing, setSyncing] = useState(false);

    const handleYearChange = (value) => {
        router.get('/monthly-reports', { year: value }, { preserveState: true });
    };

    const handleSync = () => {
        setSyncing(true);
        router.get('/monthly-reports', { year: selectedYear }, {
            preserveState: true,
            onFinish: () => setSyncing(false),
        });
    };

    return (
        <AppLayout>
            <Head title={`Monthly Reports - ${selectedYear}`} />

            <div className="space-y-6">
                <PageHeader
                    title="Monthly Reports"
                    subtitle={`Timesheet monthly summaries for ${selectedYear}`}
                />

                {/* Year Selector */}
                <Card>
                    <CardBody>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <CalendarDaysIcon className="w-5 h-5 text-slate-400" />
                                <span className="text-sm font-medium text-slate-600">Year</span>
                                <FormSelect
                                    value={selectedYear}
                                    onChange={e => handleYearChange(e.target.value)}
                                    className="w-36"
                                >
                                    {availableYears.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </FormSelect>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleSync}
                                disabled={syncing}
                            >
                                <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                                {syncing ? 'Syncing...' : 'Sync'}
                            </Button>
                        </div>
                    </CardBody>
                </Card>

                {/* Monthly Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {months.map((m) => {
                        const isFuture = m.is_future;
                        const isCurrent = m.is_current;
                        const isCompleted = m.is_completed;
                        const hasData = m.timesheet_count > 0;
                        const canDownload = m.can_download;

                        let borderColor = 'border-slate-200';
                        let bgColor = 'bg-white';
                        if (isCurrent) {
                            borderColor = 'border-emerald-300';
                            bgColor = 'bg-emerald-50/30';
                        } else if (isFuture) {
                            bgColor = 'bg-slate-50/50';
                            borderColor = 'border-slate-100';
                        } else if (isCompleted && hasData) {
                            borderColor = 'border-slate-200';
                        }

                        return (
                            <div
                                key={m.month}
                                className={`rounded-2xl border ${borderColor} ${bgColor} overflow-hidden transition-all hover:shadow-md`}
                            >
                                {/* Month Header */}
                                <div className={`px-5 py-3 border-b flex items-center justify-between ${
                                    isCurrent
                                        ? 'bg-emerald-600 border-emerald-600'
                                        : isCompleted && hasData
                                            ? 'bg-slate-700 border-slate-700'
                                            : 'bg-slate-100 border-slate-200'
                                }`}>
                                    <div className="flex items-center gap-2">
                                        <CalendarDaysIcon className={`w-4 h-4 ${
                                            isCurrent || (isCompleted && hasData) ? 'text-white/70' : 'text-slate-400'
                                        }`} />
                                        <span className={`font-bold text-sm tracking-wide ${
                                            isCurrent || (isCompleted && hasData) ? 'text-white' : 'text-slate-500'
                                        }`}>
                                            {m.label}
                                        </span>
                                    </div>
                                    {isCurrent && (
                                        <span className="text-[10px] font-bold bg-white/20 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
                                            Current
                                        </span>
                                    )}
                                    {isCompleted && hasData && (
                                        <CheckCircleIcon className="w-4 h-4 text-emerald-300" />
                                    )}
                                    {isFuture && (
                                        <LockClosedIcon className="w-3.5 h-3.5 text-slate-300" />
                                    )}
                                </div>

                                {/* Month Body */}
                                <div className="px-5 py-4">
                                    {isFuture ? (
                                        <div className="text-center py-4">
                                            <LockClosedIcon className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                                            <p className="text-xs text-slate-400">Upcoming</p>
                                        </div>
                                    ) : isCurrent ? (
                                        <div>
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                <div className="text-center p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                                                    <p className="text-[10px] text-emerald-600 font-semibold uppercase">Timesheets</p>
                                                    <p className="text-lg font-bold text-emerald-800">{m.timesheet_count}</p>
                                                </div>
                                                <div className="text-center p-2 rounded-lg bg-slate-50 border border-slate-100">
                                                    <p className="text-[10px] text-slate-500 font-semibold uppercase">Employees</p>
                                                    <p className="text-lg font-bold text-slate-700">{m.employee_count}</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                <div className="text-center p-2 rounded-lg bg-blue-50 border border-blue-100">
                                                    <p className="text-[10px] text-blue-600 font-semibold uppercase">ORD Hrs</p>
                                                    <p className="text-sm font-bold text-blue-800">{m.total_ord}</p>
                                                </div>
                                                <div className="text-center p-2 rounded-lg bg-amber-50 border border-amber-100">
                                                    <p className="text-[10px] text-amber-600 font-semibold uppercase">OT Hrs</p>
                                                    <p className="text-sm font-bold text-amber-800">{m.total_ot}</p>
                                                </div>
                                            </div>
                                            {hasData ? (
                                                <a
                                                    href={`/monthly-reports/pdf?year=${m.year}&month=${m.month}`}
                                                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded-xl transition-colors"
                                                >
                                                    <ArrowDownTrayIcon className="w-4 h-4" />
                                                    Download PDF
                                                </a>
                                            ) : (
                                                <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200">
                                                    <ClockIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                    <p className="text-xs text-slate-500">No timesheets yet</p>
                                                </div>
                                            )}
                                        </div>
                                    ) : hasData ? (
                                        <div>
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                <div className="text-center p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                                                    <p className="text-[10px] text-emerald-600 font-semibold uppercase">Timesheets</p>
                                                    <p className="text-lg font-bold text-emerald-800">{m.timesheet_count}</p>
                                                </div>
                                                <div className="text-center p-2 rounded-lg bg-slate-50 border border-slate-100">
                                                    <p className="text-[10px] text-slate-500 font-semibold uppercase">Employees</p>
                                                    <p className="text-lg font-bold text-slate-700">{m.employee_count}</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 mb-4">
                                                <div className="text-center p-2 rounded-lg bg-blue-50 border border-blue-100">
                                                    <p className="text-[10px] text-blue-600 font-semibold uppercase">ORD Hrs</p>
                                                    <p className="text-sm font-bold text-blue-800">{m.total_ord}</p>
                                                </div>
                                                <div className="text-center p-2 rounded-lg bg-amber-50 border border-amber-100">
                                                    <p className="text-[10px] text-amber-600 font-semibold uppercase">OT Hrs</p>
                                                    <p className="text-sm font-bold text-amber-800">{m.total_ot}</p>
                                                </div>
                                            </div>
                                            <a
                                                href={`/monthly-reports/pdf?year=${m.year}&month=${m.month}`}
                                                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-xl transition-colors"
                                            >
                                                <ArrowDownTrayIcon className="w-4 h-4" />
                                                Download PDF
                                            </a>
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <ExclamationTriangleIcon className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                                            <p className="text-xs text-slate-400">No timesheets</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </AppLayout>
    );
}
